/**
 * 用户实例管理的状态文案、标签颜色（antd 预设色名）和格式化函数（旧 composables/useFormatters.ts）
 */
type BadgeStatus = 'success' | 'processing' | 'default' | 'error' | 'warning'

export const INSTANCE_STATUS_OPTIONS = [
  { value: 'active', label: '进行中' },
  { value: 'paused', label: '已暂停' },
  { value: 'completed', label: '已完成' }
]

const INSTANCE_STATUS: Record<string, { label: string; badge: BadgeStatus }> = {
  active: { label: '进行中', badge: 'processing' },
  paused: { label: '已暂停', badge: 'warning' },
  completed: { label: '已完成', badge: 'success' }
}

export function instanceStatusLabel(status: string) {
  return INSTANCE_STATUS[status]?.label ?? status
}

export function instanceStatusBadge(status: string): BadgeStatus {
  return INSTANCE_STATUS[status]?.badge ?? 'default'
}

const LEVELS: Record<string, { label: string; color: string }> = {
  beginner: { label: '初级', color: 'green' },
  intermediate: { label: '中级', color: 'blue' },
  advanced: { label: '高级', color: 'orange' }
}

export function levelLabel(level: string) {
  return LEVELS[level]?.label ?? level
}

export function levelColor(level: string) {
  return LEVELS[level]?.color ?? 'default'
}

// 篇章列表与篇章详情里的状态文案沿用旧版各自的写法
const EPISODE_STATUS_BADGE: Record<string, BadgeStatus> = {
  locked: 'default',
  reading: 'processing',
  ready_to_generate: 'warning',
  completed: 'success'
}

const EPISODE_LIST_LABELS: Record<string, string> = {
  locked: '未解锁',
  reading: '正在阅读',
  ready_to_generate: '待生成',
  completed: '已完成'
}

const EPISODE_DETAIL_LABELS: Record<string, string> = {
  locked: '已锁定',
  reading: '阅读中',
  ready_to_generate: '待生成',
  completed: '已完成'
}

export function episodeStatusBadge(status: string): BadgeStatus {
  return EPISODE_STATUS_BADGE[status] ?? 'default'
}

export function episodeListLabel(status: string) {
  return EPISODE_LIST_LABELS[status] ?? status
}

export function episodeDetailLabel(status: string) {
  return EPISODE_DETAIL_LABELS[status] ?? status
}

export function coverageRate(record: { totalWordCount?: number; knownWordCount?: number }) {
  if (!record.totalWordCount) return '0.00'
  return (((record.knownWordCount ?? 0) / record.totalWordCount) * 100).toFixed(2)
}

export function formatDate(value?: string | null) {
  if (!value) return '-'
  return new Date(value).toLocaleString('zh-CN')
}

export function formatReadingTime(seconds?: number | null) {
  if (!seconds) return '-'
  return `${Math.floor(seconds / 60)}分${seconds % 60}秒`
}

const DAY_MS = 1000 * 60 * 60 * 24

// 学习天数：开始到完成（未完成则到现在），不足一天按一天
export function learningDays(stats: { startedAt?: string | null; completedAt?: string | null }, now = new Date()) {
  if (!stats.startedAt) return 0
  const end = stats.completedAt ? new Date(stats.completedAt) : now
  return Math.ceil((end.getTime() - new Date(stats.startedAt).getTime()) / DAY_MS)
}

// 对象是否有内容（空对象、空值、非对象都算没有）
export function hasContent(value: unknown) {
  return !!value && typeof value === 'object' && Object.keys(value).length > 0
}
